// GOV-001 P7 — Reporting Hierarchy Audit
//
// Reads /hr/org/reporting-hierarchy-audit. The employee
// reporting_supervisor_id chain is what the approval engine walks for
// REPORTING_SUPERVISOR approvers and what task escalation climbs — a
// break here means an approval silently routes to nobody. This page
// surfaces every break with severity so HR can fix the master data.
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Button, Card, Col, Input, Modal, Popconfirm, Progress, Row, Select,
  Space, Statistic, Table, Tabs, Tag, Typography, message,
} from 'antd'
import {
  ApartmentOutlined, DownloadOutlined, SyncOutlined, WarningOutlined,
} from '@ant-design/icons'
import apiClient from '@/shared/api/client'
import { usePermission } from '@/shared/hooks/usePermission'

const { Title, Text } = Typography

interface AuditIssue {
  check: string
  severity: 'HIGH' | 'MEDIUM' | 'LOW'
  employee_id: string
  emp_code: string
  emp_name: string
  designation: string | null
  department: string | null
  detail: string
}

interface AuditResult {
  summary: {
    active_employees: number
    with_supervisor: number
    coverage_pct: number
    max_chain_depth: number
    issue_counts: Record<string, number>
    total_issues: number
    high: number
    medium: number
    low: number
  }
  issues: AuditIssue[]
}

const SEVERITY_COLOR: Record<string, string> = {
  HIGH: 'red', MEDIUM: 'orange', LOW: 'blue',
}

const CHECK_LABEL: Record<string, string> = {
  MISSING_SUPERVISOR: 'Missing Supervisor',
  SELF_REPORTING: 'Self-Reporting',
  CIRCULAR_CHAIN: 'Circular Chain',
  INACTIVE_SUPERVISOR: 'Inactive Supervisor',
  SUPERVISOR_NO_USER: 'Supervisor Has No Login',
  CROSS_PLANT: 'Cross-Plant Supervisor',
  DEEP_CHAIN: 'Very Deep Chain',
}

// ── AUTH-001 — supervisor mapping suggestion workflow ──────────────

interface Suggestion {
  id: string
  employee_id: string
  employee_code: string | null
  employee_name: string | null
  department: string | null
  designation: string | null
  suggested_supervisor_id: string | null
  suggested_supervisor_name: string | null
  suggested_supervisor_code: string | null
  confidence: string
  reason: string | null
  status: string
  rejection_reason: string | null
}

const CONF_COLOR: Record<string, string> = {
  EXACT: 'green', SUGGESTED: 'blue', NEEDS_HR_REVIEW: 'orange',
  DATA_NOT_AVAILABLE: 'default',
}
const SUG_STATUS_COLOR: Record<string, string> = {
  MISSING: 'default', SUGGESTED: 'blue', HR_REVIEW_PENDING: 'orange',
  APPROVED: 'cyan', APPLIED: 'green', REJECTED: 'red',
  DATA_NOT_AVAILABLE: 'default',
}

function SupervisorMappingTab() {
  const qc = useQueryClient()
  const canApprove = usePermission('hr.supervisor_mapping.approve')
  const canReview = usePermission('employee.update')
  const [rejectFor, setRejectFor] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const sugQ = useQuery<{ stats: { active_employees: number
    with_supervisor: number; missing: number; coverage_pct: number }
    items: Suggestion[] }>({
    queryKey: ['supervisor-suggestions'],
    queryFn: () => apiClient.get('/hr/org/supervisor-suggestions')
      .then((r: any) => r.data),
  })

  const empQ = useQuery<{ items: { id: string; emp_code: string
    emp_name: string }[] }>({
    queryKey: ['employees-for-supervisor-pick'],
    queryFn: () => apiClient.get('/hr/employees',
      { params: { size: 500, is_active: true } })
      .then((r: any) => r.data),
  })

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['supervisor-suggestions'] })

  const genMut = useMutation({
    mutationFn: () =>
      apiClient.post('/hr/org/supervisor-suggestions/generate')
        .then((r: { data: { generated: number } }) => r.data),
    onSuccess: (r) => {
      invalidate()
      message.success(`Generated ${r.generated} suggestions — HR review required before anything is applied`)
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Generate failed'),
  })

  const reviewMut = useMutation({
    mutationFn: ({ id, supervisorId }: { id: string; supervisorId: string }) =>
      apiClient.patch(`/hr/org/supervisor-suggestions/${id}`,
        { suggested_supervisor_id: supervisorId }),
    onSuccess: () => { invalidate(); message.success('Updated — pending HR Manager approval') },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Update failed'),
  })

  const approveMut = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`/hr/org/supervisor-suggestions/${id}/approve`),
    onSuccess: () => { invalidate(); message.success('Approved & applied to the employee record') },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Approve failed'),
  })

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiClient.post(`/hr/org/supervisor-suggestions/${id}/reject`,
        { reason }),
    onSuccess: () => {
      invalidate(); setRejectFor(null); setRejectReason('')
      message.info('Suggestion rejected')
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Reject failed'),
  })

  const stats = sugQ.data?.stats
  const empOptions = (empQ.data?.items ?? []).map(e => ({
    value: e.id, label: `${e.emp_code} — ${e.emp_name}`,
  }))

  const columns = [
    { title: 'Emp Code', dataIndex: 'employee_code', key: 'employee_code', width: 100 },
    { title: 'Name', dataIndex: 'employee_name', key: 'employee_name', width: 170 },
    { title: 'Department', dataIndex: 'department', key: 'department', width: 130 },
    { title: 'Designation', dataIndex: 'designation', key: 'designation', width: 150,
      render: (v: string | null) => v ?? <Text type="secondary">—</Text> },
    {
      title: 'Suggested Supervisor', key: 'supervisor', width: 240,
      render: (_: unknown, r: Suggestion) =>
        ['APPLIED', 'REJECTED'].includes(r.status) ? (
          <Text>{r.suggested_supervisor_name ?? '—'}</Text>
        ) : (
          <Select
            showSearch optionFilterProp="label" style={{ width: 230 }}
            placeholder="Pick supervisor" size="small"
            disabled={!canReview}
            value={r.suggested_supervisor_id ?? undefined}
            options={empOptions}
            onChange={(v: string) =>
              reviewMut.mutate({ id: r.id, supervisorId: v })}
          />
        ),
    },
    {
      title: 'Confidence', dataIndex: 'confidence', key: 'confidence',
      width: 150,
      render: (v: string) =>
        <Tag color={CONF_COLOR[v]}>{v.replace(/_/g, ' ')}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 150,
      render: (v: string) =>
        <Tag color={SUG_STATUS_COLOR[v]}>{v.replace(/_/g, ' ')}</Tag>,
    },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true },
    {
      title: 'HR Action', key: 'act', width: 170, fixed: 'right' as const,
      render: (_: unknown, r: Suggestion) => {
        if (['APPLIED', 'REJECTED'].includes(r.status)) return null
        if (!canApprove) {
          return <Text type="secondary">HR Manager approval</Text>
        }
        return (
          <Space>
            <Popconfirm
              title="Apply this mapping?"
              description="Sets the employee's reporting supervisor."
              onConfirm={() => approveMut.mutate(r.id)}
            >
              <Button size="small" type="primary"
                disabled={!r.suggested_supervisor_id}
                loading={approveMut.isPending}>Approve</Button>
            </Popconfirm>
            <Button size="small" danger
              onClick={() => setRejectFor(r.id)}>Reject</Button>
          </Space>
        )
      },
    },
  ]

  return (
    <div>
      <Alert
        type="warning" showIcon style={{ marginBottom: 12 }}
        message="Suggestions never auto-apply"
        description="Generated mappings are machine guesses from the designation hierarchy. Nothing touches an employee record until an HR Manager approves the row."
      />
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col span={5}>
          <Card size="small"><Statistic title="Active Employees"
            value={stats?.active_employees ?? 0} /></Card>
        </Col>
        <Col span={5}>
          <Card size="small"><Statistic title="Mapped"
            value={stats?.with_supervisor ?? 0} /></Card>
        </Col>
        <Col span={5}>
          <Card size="small"><Statistic title="Missing"
            value={stats?.missing ?? 0}
            valueStyle={{ color: (stats?.missing ?? 0) > 0 ? '#cf1322' : undefined }} /></Card>
        </Col>
        <Col span={9}>
          <Card size="small">
            <Statistic title="Coverage" value={stats?.coverage_pct ?? 0}
              suffix="%" />
            <Progress percent={stats?.coverage_pct ?? 0} size="small"
              showInfo={false}
              status={(stats?.coverage_pct ?? 0) >= 95 ? 'success' : 'exception'} />
          </Card>
        </Col>
      </Row>
      <Card
        size="small"
        title={`Suggestions (${sugQ.data?.items.length ?? 0})`}
        extra={
          <Space>
            <Button icon={<SyncOutlined />} loading={genMut.isPending}
              disabled={!canReview}
              onClick={() => genMut.mutate()}>Generate Suggestions</Button>
            <Button icon={<DownloadOutlined />}
              href="/api/v1/hr/org/supervisor-suggestions/export.csv"
              target="_blank">Export CSV</Button>
          </Space>
        }
      >
        <Table
          rowKey="id" size="small" loading={sugQ.isLoading}
          dataSource={sugQ.data?.items ?? []} columns={columns as never}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 1300 }}
          locale={{ emptyText: 'No suggestions — click Generate Suggestions' }}
        />
      </Card>
      <Modal
        open={!!rejectFor} title="Reject suggestion"
        okText="Reject" okButtonProps={{ danger: true }}
        onOk={() => {
          if (rejectReason.trim().length < 3) {
            message.warning('Please give a reason'); return
          }
          rejectMut.mutate({ id: rejectFor!, reason: rejectReason })
        }}
        onCancel={() => { setRejectFor(null); setRejectReason('') }}
      >
        <Input.TextArea rows={3} value={rejectReason}
          onChange={e => setRejectReason(e.target.value)}
          placeholder="Why is this mapping wrong?" />
      </Modal>
    </div>
  )
}

export default function ReportingHierarchyAuditPage() {
  const [checkFilter, setCheckFilter] = useState<string | undefined>()
  const [severityFilter, setSeverityFilter] = useState<string | undefined>()

  const auditQ = useQuery<AuditResult>({
    queryKey: ['reporting-hierarchy-audit'],
    queryFn: () =>
      apiClient.get('/hr/org/reporting-hierarchy-audit')
        .then((r: { data: AuditResult }) => r.data),
  })

  const issues = useMemo(() => {
    let rows = auditQ.data?.issues ?? []
    if (checkFilter) rows = rows.filter(i => i.check === checkFilter)
    if (severityFilter) rows = rows.filter(i => i.severity === severityFilter)
    return rows
  }, [auditQ.data, checkFilter, severityFilter])

  const s = auditQ.data?.summary

  const columns = [
    {
      title: 'Severity', dataIndex: 'severity', key: 'severity', width: 100,
      render: (v: string) => <Tag color={SEVERITY_COLOR[v]}>{v}</Tag>,
    },
    {
      title: 'Issue', dataIndex: 'check', key: 'check', width: 200,
      render: (v: string) => CHECK_LABEL[v] ?? v,
    },
    { title: 'Emp Code', dataIndex: 'emp_code', key: 'emp_code', width: 110 },
    { title: 'Name', dataIndex: 'emp_name', key: 'emp_name', width: 180 },
    {
      title: 'Designation', dataIndex: 'designation', key: 'designation',
      width: 160, render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
    },
    {
      title: 'Department', dataIndex: 'department', key: 'department',
      width: 140, render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
    },
    { title: 'Detail', dataIndex: 'detail', key: 'detail' },
  ]

  return (
    <div style={{ padding: 16 }}>
      <Space align="center" style={{ marginBottom: 12 }}>
        <ApartmentOutlined style={{ fontSize: 22 }} />
        <Title level={4} style={{ margin: 0 }}>Reporting Hierarchy Audit</Title>
      </Space>

      <Tabs
        defaultActiveKey="issues"
        items={[
          {
            key: 'issues', label: 'Chain Issues',
            children: (
              <>
          <Alert
            type="info" showIcon style={{ marginBottom: 16 }}
            message="Why this matters"
            description="The reporting chain drives approval routing and task escalation. A HIGH issue means an approval or escalation involving that employee can dead-end silently."
          />

          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col span={4}>
              <Card size="small"><Statistic title="Active Employees" value={s?.active_employees ?? 0} /></Card>
            </Col>
            <Col span={5}>
              <Card size="small">
                <Statistic title="Supervisor Coverage" value={s?.coverage_pct ?? 0} suffix="%" />
                <Progress percent={s?.coverage_pct ?? 0} size="small" showInfo={false}
                  status={(s?.coverage_pct ?? 0) >= 95 ? 'success' : 'exception'} />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small">
                <Statistic title="High" value={s?.high ?? 0}
                  valueStyle={{ color: (s?.high ?? 0) > 0 ? '#cf1322' : undefined }}
                  prefix={(s?.high ?? 0) > 0 ? <WarningOutlined /> : undefined} />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small"><Statistic title="Medium" value={s?.medium ?? 0}
                valueStyle={{ color: (s?.medium ?? 0) > 0 ? '#d46b08' : undefined }} /></Card>
            </Col>
            <Col span={3}>
              <Card size="small"><Statistic title="Low" value={s?.low ?? 0} /></Card>
            </Col>
            <Col span={4}>
              <Card size="small"><Statistic title="Max Chain Depth" value={s?.max_chain_depth ?? 0} /></Card>
            </Col>
          </Row>

          <Card
            size="small"
            title={`Issues (${issues.length})`}
            extra={
              <Space>
                <Select
                  allowClear placeholder="Issue type" style={{ width: 220 }}
                  value={checkFilter} onChange={setCheckFilter}
                  options={Object.entries(CHECK_LABEL).map(([value, label]) => ({
                    value,
                    label: `${label} (${auditQ.data?.summary.issue_counts[value] ?? 0})`,
                  }))}
                />
                <Select
                  allowClear placeholder="Severity" style={{ width: 120 }}
                  value={severityFilter} onChange={setSeverityFilter}
                  options={['HIGH', 'MEDIUM', 'LOW'].map(v => ({ value: v, label: v }))}
                />
              </Space>
            }
          >
            <Table
              rowKey={(r: AuditIssue) => `${r.employee_id}:${r.check}`}
              loading={auditQ.isLoading}
              dataSource={issues}
              columns={columns}
              size="small"
              pagination={{ pageSize: 20, showSizeChanger: true }}
              locale={{ emptyText: auditQ.isLoading ? ' ' : 'No hierarchy issues — chain is clean ✓' }}
            />
          </Card>
              </>
            ),
          },
          {
            key: 'mapping', label: 'Supervisor Mapping',
            children: <SupervisorMappingTab />,
          },
        ]}
      />
    </div>
  )
}
